import api from './api';

let scriptLoadingPromise = null;

/**
 * Injects the Razorpay Checkout script exactly once and resolves when ready.
 */
export const loadRazorpayScript = () => {
  if (window.Razorpay) return Promise.resolve(true);
  if (scriptLoadingPromise) return scriptLoadingPromise;

  scriptLoadingPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  return scriptLoadingPromise;
};

/**
 * POST /api/payments/create-order
 * amount: rupees (e.g. 499.00)
 * purpose: free-form label, e.g. "APPOINTMENT" | "PHARMACY_ORDER"
 * Returns { razorpayOrderId, razorpayKeyId, amount (paise), currency }
 */
export const createRazorpayOrder = async (amount, purpose) => {
  const { data } = await api.post('/payments/create-order', { amount, purpose });
  return data;
};

/**
 * Opens the Razorpay Checkout widget for the given order and resolves with
 * the payment proof on success, or rejects if the user cancels / it fails.
 *
 * @param {Object} opts
 * @param {number} opts.amount        - rupees, just for display in the widget
 * @param {string} opts.purpose       - "APPOINTMENT" | "PHARMACY_ORDER"
 * @param {Object} [opts.prefill]     - { name, email, contact }
 * @param {string} [opts.description] - shown inside the widget
 * @param {string} [opts.tenantName]  - tenant display name, shown as the widget title
 */
export const payWithRazorpay = async ({ amount, purpose, prefill = {}, description, tenantName }) => {
  const scriptReady = await loadRazorpayScript();
  if (!scriptReady) {
    throw new Error('Could not load Razorpay. Check your internet connection and try again.');
  }

  const order = await createRazorpayOrder(amount, purpose);

  return new Promise((resolve, reject) => {
    const options = {
      key: order.razorpayKeyId,
      amount: order.amount,
      currency: order.currency,
      name: tenantName || 'ArogyaOne',
      description: description || 'Payment',
      order_id: order.razorpayOrderId,
      prefill,
      theme: { color: '#4f46e5' },
      handler: (response) => {
        resolve({
          razorpayOrderId: response.razorpay_order_id,
          razorpayPaymentId: response.razorpay_payment_id,
          razorpaySignature: response.razorpay_signature,
        });
      },
      modal: {
        ondismiss: () => reject(new Error('Payment was cancelled')),
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (resp) => {
      reject(new Error(resp?.error?.description || 'Payment failed. Please try again.'));
    });
    rzp.open();
  });
};